import {AsyncSubject, Observable} from 'rxjs';
import {AbstractDataSource, FlexibleAbstractDataSource} from './abstract.datasource';
import {CrudService, FlexibleCrudService} from '../services/index';
import {Order} from '../dto/order';
import {DataListModel} from '../models/data-list-model';
import {AppUserPartnerWalletAccessSlim} from '../dto/app-user-partner-wallet-access-slim';

/**
 * Data sorce provide helper class, incapsulate
 * delete single or multiple rows,
 * obtain data
 */
export class MultiCrudDataSource<T> extends AbstractDataSource<T> {

  getManyOverride: (
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
    dateFilterCols: string[],
    startFilterDate: Date,
    endFilterDate: Date,
    someAdditionalData: string,
    additionalFilterData?: string[],
    enumFilter?: {key: string; value: string},
    companiesFilter?: string[],
    additionalField1?: string,
    additionalField2?: string,
    additionalValue1?: string,
    additionalValue2?: string,
    identStatusFilter?: string[],
    productsFilter?: string[],
    cardStatusFilter?: string[],
    countriesFilter?: string[],
  ) => Observable<DataListModel<T>>;

  constructor(private service: CrudService<T>, options?: MultiCrudDateSourceOptions) {
    super();
    if (options && options.getManyOverride != null) {
      this.getManyOverride = options.getManyOverride;
    }


  }
  /**
   * Call delete selected row from server by specific id
   *
   * @param id row id
   */
  delete(id: number): Observable<any> {
    return this.service.delete(id);
  }
  /**
   * Call resotore selected row from server by specific id
   *
   * @param id row id
   */
  restore(id: number): Observable<any> {
    return this.service.restore(id);
  }
  /**
   * Call delete selected rows from server by specific ids
   *
   * @param id row id
   */
  deleteMany(list: any[]): Observable<any> {
    return this.service.deleteMany(list);
  }
  /**
   * Call resotore selected rows from server by specific ids
   *
   * @param id row id
   */
  restoreMany(list: any[]): Observable<any> {
    return this.service.restoreMany(list);
  }
  /**
   * Getting data from data source, with specific page settings and filter
   *
   * @param pagingAndSortingData Page number, rows per page, sort field and direction
   */
  getData(pagingAndSortingData: any): Observable<any> {
    const str = this._filter.str || '';
    const dateFilterCols = this._filter.dateFilterCols || [];
    const startFilterDate = this._filter.startFilterDate || null;
    const endFilterDate = this._filter.endFilterDate || null;
    const someAdditionalData = this._filter.someAdditionalData || '';
    const additionalFilterData = this._filter.additionalFilterData || [];
    const enumFilter = this._filter.enumFilter || null;
    const companiesFilter = this._filter.companiesFilter || [];

    const additionalField1 = this._filter.additionalField1 || null;
    const additionalField2 = this._filter.additionalField2 || null;
    const additionalValue1 = this._filter.additionalValue1 || null;
    const additionalValue2 = this._filter.additionalValue2 || null;

    const identStatusFilter = this._filter.identStatusFilter || [];

    const productsFilter = this._filter.productsFilter || [];
    const cardStatusFilter = this._filter.cardStatusFilter || [];
    const countriesFilter = this._filter.countriesFilter || [];

    if ( this.getManyOverride ) {
      return this.getManyOverride(
        pagingAndSortingData.pageIndex,
        pagingAndSortingData.pageSize,
        pagingAndSortingData.sortField,
        pagingAndSortingData.sortDirection,
        str,
        dateFilterCols,
        startFilterDate,
        endFilterDate,
        someAdditionalData,
        additionalFilterData,
        enumFilter,
        companiesFilter,
        additionalField1,
        additionalField2,
        additionalValue1,
        additionalValue2,
        identStatusFilter,
        productsFilter,
        cardStatusFilter,
        countriesFilter,

      );
    }

    return this.service.getMany(
      pagingAndSortingData.pageIndex,
      pagingAndSortingData.pageSize,
      pagingAndSortingData.sortField,
      pagingAndSortingData.sortDirection,
      str,
      dateFilterCols,
      startFilterDate,
      endFilterDate,
      someAdditionalData,
      additionalFilterData,
      enumFilter,
      companiesFilter,
      additionalField1,
      additionalField2,
      additionalValue1,
      additionalValue2,
      identStatusFilter,
      productsFilter,
      cardStatusFilter,
      countriesFilter,

    );
  }
}

export class MultiCrudDateSourceOptions {
  getManyOverride: (pageIndex: number, pageSize: number, sortField: string, sortDirection: string, filterString: string, dateFilterCols: string[], startFilterDate: Date, endFilterDate: Date, someAdditionalData: string, additionalFilterData?: string[], enumFilter?: {key: string; value: string}, companiesFilter?: string[]) => Observable<DataListModel<any>>;
}


export class ReadOnlyDataSource<T> extends AbstractDataSource<T> {
  private readonly getManyFunction: () => Observable<DataListModel<T>>;

  constructor(getMany: () => Observable<DataListModel<T>>) {
    super();
    this.getManyFunction = getMany;
  }

  getData(pagingAndSortingData: any): Observable<DataListModel<T>> {
    return this.getManyFunction().pipe();
  }

}

export class FlexibleMultiCrudDataSource<TList, TGet, TAdd, TUpdate> extends FlexibleAbstractDataSource<TList, TGet, TAdd, TUpdate> {

  getManyOverride: (
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
    dateFilterCols: string[],
    startFilterDate: Date,
    endFilterDate: Date,
    someAdditionalData: string,
    additionalFilterData?: string[],
    enumFilter?: {key: string; value: string},
    companiesFilter?: string[],
    additionalField1?: string,
    additionalField2?: string,
    additionalValue1?: string,
    additionalValue2?: string
  ) => Observable<DataListModel<Order>>;

  constructor(private service: FlexibleCrudService<TList, TGet, TAdd, TUpdate>, options?: MultiCrudDateSourceOptions) {
    super();
    if (options && options.getManyOverride != null) {
      this.getManyOverride = options.getManyOverride;
    }


  }
  /**
   * Call delete selected row from server by specific id
   *
   * @param id row id
   */
  delete(id: number): Observable<any> {
    return this.service.delete(id);
  }
  /**
   * Call resotore selected row from server by specific id
   *
   * @param id row id
   */
  restore(id: number): Observable<any> {
    return this.service.restore(id);
  }
  /**
   * Call delete selected rows from server by specific ids
   *
   * @param id row id
   */
  deleteMany(list: any[]): Observable<any> {
    return this.service.deleteMany(list);
  }
  /**
   * Call resotore selected rows from server by specific ids
   *
   * @param id row id
   */
  restoreMany(list: any[]): Observable<any> {
    return this.service.restoreMany(list);
  }
  /**
   * Getting data from data source, with specific page settings and filter
   *
   * @param pagingAndSortingData Page number, rows per page, sort field and direction
   */
  getData(pagingAndSortingData: any): Observable<any> {
    const str = this._filter.str || '';
    const dateFilterCols = this._filter.dateFilterCols || [];
    const startFilterDate = this._filter.startFilterDate || null;
    const endFilterDate = this._filter.endFilterDate || null;
    const someAdditionalData = this._filter.someAdditionalData || '';
    const additionalFilterData = this._filter.additionalFilterData || [];
    const enumFilter = this._filter.enumFilter || null;
    const companiesFilter = this._filter.companiesFilter || [];

    const additionalField1 = this._filter.additionalField1 || null;
    const additionalField2 = this._filter.additionalField2 || null;
    const additionalValue1 = this._filter.additionalValue1 || null;
    const additionalValue2 = this._filter.additionalValue2 || null;

    if ( this.getManyOverride ) {
      return this.getManyOverride(
        pagingAndSortingData.pageIndex,
        pagingAndSortingData.pageSize,
        pagingAndSortingData.sortField,
        pagingAndSortingData.sortDirection,
        str,
        dateFilterCols,
        startFilterDate,
        endFilterDate,
        someAdditionalData,
        additionalFilterData,
        enumFilter,
        companiesFilter,
        additionalField1,
        additionalField2,
        additionalValue1,
        additionalValue2

      );
    }

    return this.service.getMany(
      pagingAndSortingData.pageIndex,
      pagingAndSortingData.pageSize,
      pagingAndSortingData.sortField,
      pagingAndSortingData.sortDirection,
      str,
      dateFilterCols,
      startFilterDate,
      endFilterDate,
      someAdditionalData,
      additionalFilterData,
      enumFilter,
      companiesFilter,
      additionalField1,
      additionalField2,
      additionalValue1,
      additionalValue2

    );
  }
}


